import { useMemo } from "react";
import {
  useExternalItemsControllerGetItems,
  type ExternalItemsControllerGetItemsParams,
  type PaginationMetaDto,
} from "@semo/api-contracts";
import { keepPreviousData } from "@tanstack/react-query";
import { mapSupplierProductToDomain, type Product } from "@semo/shared-product";

/**
 * «내 상품» 의 판매 상태 필터 — 서버 `GET /external/items?sellingStatus=` 와 같은 값.
 *
 * - `active`   판매중
 * - `inactive` 판매중지(단가는 있지만 판매를 멈춘 내 상품)
 * - `all`      둘 다
 *
 * `onlyMyProducts` 일 때만 서버가 적용한다.
 */
export type VendorSellingStatus = "active" | "inactive" | "all";

export interface VendorProductListOptions {
  limit: number;
  offset: number;
  onlyMyProducts: boolean;
  onlyMyCategories: boolean;
  /** 2글자 미만이면 넘기지 않는다(서버 검색은 2글자부터). */
  search?: string;
  /** onlyMyProducts 일 때 서버가 거를 판매 상태. 생략하면 서버 기본(판매중). */
  sellingStatus?: VendorSellingStatus;
}

interface VendorProductListResult {
  products: Product[];
  pagination: PaginationMetaDto;
  isFetching: boolean;
}

/**
 * 상품/단가 관리 목록.
 *
 * 공용 `useSupplierProducts` 와 같지만 **판매 상태를 서버에 함께 보낸다**(GS 검수 V-08).
 * 예전에는 판매상태 필터를 지금 받아 온 100줄 안에서만 걸어, «결과 없음 + 전체 406,437개» 처럼
 * 목록과 개수·쪽수가 어긋났다. 서버가 거르면 개수·쪽수도 같은 조건으로 나온다.
 *
 * 생성 타입(`ExternalItemsControllerGetItemsParams`)에는 아직 `sellingStatus` 가 없어
 * 넓힌 타입으로 넘긴다 — 생성 fetcher 는 params 를 그대로 쿼리스트링에 싣고, 쿼리 키에도
 * 들어가 캐시가 조건별로 갈린다.
 */
export function useVendorProductList(
  categoryId: string | null,
  options: VendorProductListOptions,
): VendorProductListResult {
  const params: ExternalItemsControllerGetItemsParams & {
    sellingStatus?: VendorSellingStatus;
  } = {
    categoryId: categoryId === "1" ? undefined : (categoryId ?? undefined),
    includeDescendants: true,
    limit: options.limit,
    offset: options.offset,
    onlyMyProducts: options.onlyMyProducts,
    onlyMyCategories: options.onlyMyCategories,
    search: options.search,
    ...(options.onlyMyProducts && options.sellingStatus
      ? { sellingStatus: options.sellingStatus }
      : {}),
  };

  const { data, isFetching } = useExternalItemsControllerGetItems(params, {
    query: {
      staleTime: 5000,
      placeholderData: keepPreviousData,
    },
  });

  return useMemo(() => {
    const apiData = data?.data;
    const pagination: PaginationMetaDto = {
      totalCount: apiData?.meta.totalCount ?? 0,
      itemCount: apiData?.meta.itemCount ?? 0,
      pageSize: apiData?.meta.pageSize ?? options.limit,
      currentPage: apiData?.meta.currentPage ?? 1,
      totalPages: apiData?.meta.totalPages ?? 0,
      hasNext: apiData?.meta.hasNext ?? false,
      hasPrev: apiData?.meta.hasPrev ?? false,
    };
    return {
      products: (apiData?.items ?? []).map(mapSupplierProductToDomain),
      pagination,
      isFetching,
    };
  }, [data, isFetching, options.limit]);
}
